// web/src/components/Toast.tsx — toast(...) + <ToastHost/>. aria-live, auto-dismiss, no focus steal (D05).
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Icon } from './Icon';

type Kind = 'success' | 'error' | 'info';
interface ToastItem { id: number; kind: Kind; message: string; }

let seq = 0;
const listeners = new Set<(t: ToastItem) => void>();

export function toast(p: { kind: Kind; message: string }) {
  const item = { id: ++seq, ...p };
  listeners.forEach((l) => l(item));
}

const TONE: Record<Kind, { icon: string; cls: string }> = {
  success: { icon: 'check', cls: 'border-run text-run' },
  error: { icon: 'alert', cls: 'border-danger text-danger' },
  info: { icon: 'message', cls: 'border-info text-info' },
};

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    const l = (t: ToastItem) => {
      setItems((cur) => [...cur, t]);
      setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== t.id)), 4000);
    };
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5rem)] z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6" aria-live="polite">
      <AnimatePresence>
        {items.map((t) => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            className={`pointer-events-auto flex w-full max-w-sm items-center gap-2 rounded-lg border bg-surface px-4 py-3 text-sm text-ink shadow-lg ${TONE[t.kind].cls}`}>
            <Icon name={TONE[t.kind].icon} size={16} />
            <span className="text-ink">{t.message}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
